import { useNavigate, useSearchParams } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react'
import { Download, Upload, FileText } from 'lucide-react'
import CandidateProfileTabs from '@/components/candidate/CandidateProfileTabs'
import { apiClient } from '@/lib/api'

export default function CareerProfilePage() {
  const [currentUser, setCurrentUser] = useState<any>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadMessage, setUploadMessage] = useState('')
  const [extractedData, setExtractedData] = useState<any>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  useEffect(() => {
    // Get current user from localStorage
    const loadUserData = () => {
      if (typeof window !== 'undefined') {
        const userData = localStorage.getItem('currentUser')
        if (userData) {
          try {
            const user = JSON.parse(userData)
            setCurrentUser(user)
          } catch (error) {
            console.error('Error parsing user data:', error)
          }
        }
      }
    }
    
    loadUserData()
  }, [])

  const handleResumeUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Validate file type
    const allowedTypes = ['.pdf', '.docx', '.doc', '.txt']
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'))

    if (!allowedTypes.includes(fileExtension)) {
      setUploadMessage('Please upload a PDF, DOCX, DOC, or TXT file')
      return
    }

    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      setUploadMessage('File size too large. Maximum allowed size is 10MB')
      return
    }

    setIsUploading(true)
    setUploadMessage('Extracting resume data...')

    try {
      const response = await apiClient.uploadResumeForProfile(file)

      if (response.success) {
        console.log('✅ Resume extraction successful:', response.data)
        setExtractedData(response.data)
        setUploadMessage('Resume data extracted successfully! Review and fill your profile.')
      } else {
        setUploadMessage(response.error || 'Failed to extract resume data')
      }
    } catch (error) {
      console.error('Resume upload error:', error)
      setUploadMessage('An error occurred while uploading your resume')
    } finally {
      setIsUploading(false)
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleImportClick = () => {
    fileInputRef.current?.click()
  }

  const handleExportToResumeBuilder = async () => {
    try {
      // Fetch all profile data from backend using the proper API helpers
      const { buildApiUrl, getApiHeaders } = await import('@/config/api')
      const headers = getApiHeaders(true)

      // Fetch data from all profile endpoints
      const [basicRes, educationRes, workExpRes, skillsRes, certificationsRes] = await Promise.all([
        fetch(buildApiUrl('users/career-profile/me'), { headers }),
        fetch(buildApiUrl('education/me/profile'), { headers }),
        fetch(buildApiUrl('work-exp/me/profile/work-experience'), { headers }),
        fetch(buildApiUrl('candidates/me/profile/skills'), { headers }),
        fetch(buildApiUrl('certs/me/profile/certifications'), { headers })
      ])

      const [basicData, educationData, workExpData, skillsData, certificationsData] = await Promise.all([
        basicRes.json(),
        educationRes.json(),
        workExpRes.json(),
        skillsRes.json(),
        certificationsRes.json()
      ])

      // Combine all profile data
      const candidateProfileData = {
        source: 'career-profile',
        timestamp: Date.now(),
        basic: basicData.success ? basicData.data : null,
        education: educationData.success ? educationData.data : null,
        workExperience: workExpData.success ? workExpData.data : null,
        skills: skillsData.success ? skillsData.data : null,
        certifications: certificationsData.success ? certificationsData.data : null
      }

      // Store in sessionStorage for the resume builder to pick up
      sessionStorage.setItem('resumeBuilderData', JSON.stringify(candidateProfileData))

      // Navigate to resume builder
      navigate('/candidate/resume-builder')
    } catch (error) {
      console.error('Error fetching profile data:', error)
      setUploadMessage('Failed to export profile data. Please try again.')
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Career Profile</h1>
                <p className="text-gray-600 mt-1">Manage your professional information</p>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={handleExportToResumeBuilder}
                  className="flex items-center space-x-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:from-purple-700 hover:to-blue-700 transition-all shadow-md hover:shadow-lg"
                >
                  <Download className="w-4 h-4" />
                  <span>Get Professional Resume</span>
                </button>
                <button
                  onClick={handleImportClick}
                  disabled={isUploading}
                  className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Upload className="w-4 h-4" />
                  <span>{isUploading ? 'Processing...' : 'Import Resume'}</span>
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.docx,.doc,.txt"
                  onChange={handleResumeUpload}
                  className="hidden"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Upload Status Message */}
        {uploadMessage && (
          <div className={`mb-6 p-4 rounded-lg ${
            uploadMessage.includes('successfully')
              ? 'bg-green-50 text-green-800 border border-green-200'
              : uploadMessage.includes('error') || uploadMessage.includes('Failed')
                ? 'bg-red-50 text-red-800 border border-red-200'
                : 'bg-blue-50 text-blue-800 border border-blue-200'
          }`}>
            {uploadMessage}
          </div>
        )}

        {/* Extracted Data Preview */}
        {extractedData && (
          <div className="mb-6 p-4 rounded-lg bg-white border border-gray-200">
            <h2 className="text-lg font-semibold mb-3">Imported Resume Preview</h2>

            <div className="flex items-center justify-end mb-3">
              <button
                className="btn-primary mr-2"
                onClick={() => {
                  // Dispatch the custom event that CandidateProfileTabs listens for
                  if (typeof window !== 'undefined') {
                    window.dispatchEvent(new Event('applyExtractedResume'));
                    setUploadMessage('Applied extracted resume data to the profile tabs. Review and save.');
                    // Clear the preview so the UI switches to showing data inside the tabs only
                    setExtractedData(null);
                  }
                }}
              >
                Apply to profile
              </button>
            </div>

            {/* Basic Details */}
            <div className="mb-4">
              <h3 className="font-medium">Basic Details</h3>
              <div className="text-sm text-gray-700 mt-1">
                <div>{extractedData.basic_info?.full_name || extractedData.basic_info?.name}</div>
                <div>{extractedData.basic_info?.email}</div>
                <div>{extractedData.basic_info?.phone}</div>
                <div>{extractedData.basic_info?.current_title} {extractedData.basic_info?.current_company ? `at ${extractedData.basic_info?.current_company}` : ''}</div>
                <div className="text-xs text-gray-500">{extractedData.professional_summary}</div>
              </div>
            </div>

            {/* Work Experience */}
            <div className="mb-4">
              <h3 className="font-medium">Work Experience</h3>
              <div className="mt-2 space-y-2 text-sm text-gray-700 max-h-40 overflow-auto">
                {(extractedData.work_experience || extractedData.previous_experiences || []).map((we: any, idx: number) => (
                  <div key={`we-${idx}`} className="p-2 border rounded-md bg-gray-50">
                    <div className="font-semibold">{we.title} — {we.company}</div>
                    <div className="text-xs text-gray-500">{we.dates}</div>
                    <div className="text-sm mt-1">{we.description}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Education */}
            <div className="mb-4">
              <h3 className="font-medium">Education</h3>
              <div className="mt-2 space-y-1 text-sm text-gray-700">
                {(extractedData.education || []).map((ed: any, idx: number) => (
                  <div key={`ed-${idx}`}>{ed.degree || ed.degreeName || ed.degree_title} — {ed.institution || ed.school} {ed.year ? `(${ed.year})` : ''}</div>
                ))}
              </div>
            </div>

            {/* Skills */}
            <div className="mb-4">
              <h3 className="font-medium">Skills</h3>
              <div className="mt-2 text-sm text-gray-700">
                {(extractedData.skills || []).slice(0, 50).map((s: any, i: number) => (
                  <span key={`skill-${i}`} className="inline-block mr-2 mb-1 px-2 py-0.5 bg-blue-50 text-blue-700 rounded">{typeof s === 'string' ? s : s.name || s.skillName}</span>
                ))}
              </div>
            </div>

            {/* Awards / Certifications */}
            <div className="flex flex-col sm:flex-row sm:space-x-6">
              <div className="flex-1">
                <h3 className="font-medium">Awards</h3>
                <div className="mt-2 text-sm text-gray-700">
                  {(extractedData.awards || []).length ? (extractedData.awards || []).map((a: any, i: number) => (
                    <div key={`award-${i}`}>{typeof a === 'string' ? a : a.title}</div>
                  )) : <div className="text-xs text-gray-500">No awards found</div>}
                </div>
              </div>

              <div className="flex-1 mt-4 sm:mt-0">
                <h3 className="font-medium">Certifications</h3>
                <div className="mt-2 text-sm text-gray-700">
                  {(extractedData.certifications || []).length ? (extractedData.certifications || []).map((c: any, i: number) => (
                    <div key={`cert-${i}`}>{typeof c === 'string' ? c : (c.title || c.name)}</div>
                  )) : <div className="text-xs text-gray-500">No certifications found</div>}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Use the existing CandidateProfileTabs component */}
        <CandidateProfileTabs
          candidateUuid="me"
          isEditable={true}
          currentUser={currentUser}
          extractedResumeData={extractedData}
        />
      </div>

    </div>
  )
}